import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import WalletGearModal, { PageGearButton } from './WalletGearModal';
import HlTradingChart from './HlTradingChart';
import DeskChat from './DeskChat';
import useMarketStream from '../hooks/useMarketStream';
import { fmtUsd } from '../utils/fmtPrice';
import { downloadDiscScalpingCsv, downloadDiscScalpingPdf } from '../utils/dscExport';
import {
  getHlSpreadSteps,
  getHlSpreadUnit,
  spreadStepLabel,
  stepsEqual,
  spreadStepStorageKey,
  loadSavedSpreadStep,
  saveSpreadStep,
  aggregateBookLevels,
  snapBookPrice,
  fillBookWindow,
} from '../utils/hlSpreadSteps';
import omniMobileLogo from '../assets/omni-scalping-mobile.png';
import './DiscretionaryScalping.css';
import './WalletGearModal.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

const API = 'disc-scalping';
const LADDER_LEVELS = 12;
/** Recenter the pinned book only after the BBO walks this many steps off the inner edge. */
const LADDER_RECENTER_STEPS = 6;
const ACCOUNT_LIGHT_MS = 3000;
const ACCOUNT_FULL_MS = 20000;
const QUICK_MARKETS = [
  { symbol: 'BTC', coin: 'BTC', dex: 'main' },
  { symbol: 'ETH', coin: 'ETH', dex: 'main' },
  { symbol: 'SOL', coin: 'SOL', dex: 'main' },
  { symbol: 'HYPE', coin: 'HYPE', dex: 'main' },
];
const ORDER_TYPES = [
  { id: 'market', label: 'Market', hint: 'Aggressive limit, capped by slippage' },
  { id: 'limit', label: 'Limit', hint: 'Rests on the book (GTC)' },
  { id: 'post_only', label: 'Post-only', hint: 'Maker only — rejected if it would cross' },
  { id: 'ioc', label: 'IOC', hint: 'Fill what you can now, cancel the rest' },
];

function priceDecimals(px) {
  if (!Number.isFinite(px)) return 2;
  if (px >= 10000) return 1;
  if (px >= 1000) return 2;
  if (px >= 100) return 3;
  if (px >= 1) return 4;
  return 6;
}
function fmtPx(px, ref) {
  if (px == null || !Number.isFinite(px)) return '—';
  const d = priceDecimals(ref ?? px);
  return px.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
}
function fmtSz(sz) {
  if (sz == null || !Number.isFinite(sz)) return '—';
  if (sz >= 1000) return sz.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (sz >= 10) return sz.toFixed(2);
  if (sz >= 1) return sz.toFixed(3);
  return sz.toFixed(5).replace(/0+$/, '').replace(/\.$/, '');
}
const BOOK_SIZE_STORAGE = 'dsc-book-size-unit';
function loadBookSizeUnit() {
  try {
    const v = localStorage.getItem(BOOK_SIZE_STORAGE);
    return v === 'coin' ? 'coin' : 'usdc';
  } catch {
    return 'usdc';
  }
}
function saveBookSizeUnit(unit) {
  try {
    localStorage.setItem(BOOK_SIZE_STORAGE, unit);
  } catch {
    /* ignore */
  }
}
function bookDisplaySz(sz, px, unit) {
  if (sz == null || !Number.isFinite(sz)) return null;
  if (unit === 'usdc') {
    const ntl = Number.isFinite(px) ? sz * px : null;
    return ntl;
  }
  return sz;
}
function fmtBookSz(sz, px, unit) {
  const v = bookDisplaySz(sz, px, unit);
  if (v == null) return '—';
  return unit === 'usdc' ? fmtCompactUsd(v) : fmtSz(v);
}
function fmtCompactUsd(v) {
  if (v == null || !Number.isFinite(v)) return '—';
  if (Math.abs(v) >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return fmtUsd(v);
}
function finiteNum(v) {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}
/** Unified HL wallets report $0 on perps; pick the first real dollar amount. */
function firstPositiveUsd(...vals) {
  for (const v of vals) {
    const n = finiteNum(v);
    if (n != null && n > 0) return n;
  }
  return null;
}
function mergeLiveBalances(prev, next) {
  const out = { ...prev, ...next };
  for (const k of ['accountValueUsd', 'equityUsd', 'withdrawableUsd', 'perpsWithdrawableUsd', 'spotUsdc', 'spotUsdcTotal']) {
    const keep = finiteNum(prev?.[k]);
    const incoming = finiteNum(next?.[k]);
    if (keep != null && keep > 0 && !(incoming > 0)) out[k] = keep;
    // Light polls can send the reserved-margin slice (~$4) — keep the larger full equity.
    if ((k === 'accountValueUsd' || k === 'equityUsd' || k === 'spotUsdcTotal') && keep != null && incoming != null && keep > incoming) {
      out[k] = keep;
    }
  }
  if (prev?.unified && next?.unified == null) out.unified = prev.unified;
  if (finiteNum(prev?.spotUsdcHold) != null && next?.spotUsdcHold == null) out.spotUsdcHold = prev.spotUsdcHold;
  return out;
}
function fmtPnl(v) {
  const n = finiteNum(v);
  if (n == null) return '—';
  return `${n >= 0 ? '+' : '−'}${fmtUsd(Math.abs(n))}`;
}
/** Hyperliquid-style: +$1,234.56 (+17.6%) */
function fmtPnlRoe(pnl, roe) {
  const p = finiteNum(pnl);
  if (p == null) return '—';
  const r = finiteNum(roe);
  const pct = r != null ? ` (${r >= 0 ? '+' : '−'}${Math.abs(r * 100).toFixed(1)}%)` : '';
  return `${fmtPnl(p)}${pct}`;
}
function signedContractsOf(p) {
  const signed = finiteNum(p?.signedContracts);
  if (signed != null) return signed;
  const size = finiteNum(p?.sizeContracts);
  if (size == null) return null;
  return String(p?.side || '').toLowerCase() === 'short' ? -size : size;
}
function roeOf(p) {
  if (!p) return null;
  const stored = finiteNum(p.returnOnEquity);
  if (stored != null) return stored;
  const pnl = finiteNum(p.unrealizedPnl);
  const margin =
    finiteNum(p.marginUsed) ||
    (finiteNum(p.positionValue) > 0 && finiteNum(p.leverage) > 0
      ? finiteNum(p.positionValue) / finiteNum(p.leverage)
      : null);
  if (pnl == null || !margin || margin <= 0) return null;
  return pnl / margin;
}
/** Prefer live mark for the open market; recompute PnL so a missing mark never becomes +0.00. */
function enrichPosition(p, live = {}) {
  if (!p) return p;
  const here =
    (live.coin && p.coin === live.coin) ||
    (live.symbol && String(p.symbol || '').toUpperCase() === String(live.symbol).toUpperCase());
  const mark = (here ? finiteNum(live.mark) : null) ?? finiteNum(p.markPrice);
  const entry = finiteNum(p.entryPrice);
  const size = signedContractsOf(p);
  const pnl =
    mark != null && entry != null && size != null ? (mark - entry) * size : finiteNum(p.unrealizedPnl);
  const notional = Math.abs(size || 0) * (mark ?? entry ?? 0);
  const lev = finiteNum(p.leverage) || 1;
  const margin = finiteNum(p.marginUsed) || (notional > 0 ? notional / lev : null);
  return {
    ...p,
    markPrice: mark,
    positionValue: notional || p.positionValue,
    unrealizedPnl: pnl,
    returnOnEquity: pnl != null && margin > 0 ? pnl / margin : finiteNum(p.returnOnEquity),
  };
}
function parseTs(ts) {
  if (!ts) return null;
  const d = typeof ts === 'number' ? new Date(ts) : new Date(String(ts).includes('T') ? ts : `${String(ts).replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}
function fmtTime(ts) {
  const d = parseTs(ts);
  return d ? d.toLocaleTimeString([], { hour12: false }) : '';
}
function fmtDateTime(ts) {
  const d = parseTs(ts);
  if (!d) return '';
  const date = d.toLocaleDateString([], { month: '2-digit', day: '2-digit', year: 'numeric' });
  const time = d.toLocaleTimeString([], { hour12: false });
  return `${date} ${time}`;
}
function marketKey(m) {
  return `${(m?.dex || 'main').toLowerCase()}:${String(m?.symbol || '').toUpperCase()}`;
}

/* ------------------------------ Price ladder ------------------------------ */

function Ladder({ book, bbo, ctx, position, openOrders, onPick, flash, coin, dex, symbol }) {
  const rawMid =
    bbo?.bid?.[0] != null && bbo?.ask?.[0] != null
      ? (bbo.bid[0] + bbo.ask[0]) / 2
      : ctx?.midPx ?? book?.bids?.[0]?.[0] ?? null;
  const spreadUnit = getHlSpreadUnit(rawMid);
  const spreadSteps = useMemo(() => getHlSpreadSteps(rawMid), [spreadUnit]); // eslint-disable-line react-hooks/exhaustive-deps -- tier unit drives steps
  const storageKey = useMemo(() => spreadStepStorageKey(dex, coin), [dex, coin]);
  const [spreadStep, setSpreadStep] = useState(() => getHlSpreadSteps(null)[0]);
  const [sizeUnit, setSizeUnit] = useState(loadBookSizeUnit);
  const [pinCenter, setPinCenter] = useState(null);
  const coinLabel = String(symbol || coin || 'BTC')
    .replace(/^[^:]+:/, '')
    .toUpperCase();

  useEffect(() => {
    setSpreadStep(loadSavedSpreadStep(storageKey, getHlSpreadSteps(rawMid)));
    setPinCenter(null);
  }, [storageKey, spreadUnit]); // rawMid only via spreadUnit tier

  const selectSpreadStep = (step) => {
    setSpreadStep(step);
    saveSpreadStep(storageKey, step);
    setPinCenter(null);
  };

  const aggBidsAll = useMemo(
    () => aggregateBookLevels(book?.bids || [], spreadStep, 'bid'),
    [book, spreadStep]
  );
  const aggAsksAll = useMemo(
    () => aggregateBookLevels(book?.asks || [], spreadStep, 'ask'),
    [book, spreadStep]
  );
  const liveBid = snapBookPrice(bbo?.bid?.[0] ?? aggBidsAll[0]?.[0], spreadStep, 'bid');
  const liveAsk = snapBookPrice(bbo?.ask?.[0] ?? aggAsksAll[0]?.[0], spreadStep, 'ask');
  const liveCenter = snapBookPrice(rawMid, spreadStep, 'bid') ?? liveBid;

  useEffect(() => {
    if (!(liveCenter > 0) || !(spreadStep > 0)) return undefined;
    setPinCenter((prev) => {
      if (prev == null || prev.step !== spreadStep) return { px: liveCenter, step: spreadStep };
      const away = Math.abs(liveCenter - prev.px) / spreadStep;
      // Follow the live mid; a stale book snapshot used to pin the ladder $200 away.
      if (away > LADDER_RECENTER_STEPS) return { px: liveCenter, step: spreadStep };
      return prev;
    });
  }, [liveCenter, spreadStep]);

  const centerPx = pinCenter?.step === spreadStep ? pinCenter.px : liveCenter;
  const askStart = centerPx != null && spreadStep > 0 ? snapBookPrice(centerPx + spreadStep, spreadStep, 'ask') : null;
  const bids = useMemo(
    () => (centerPx != null ? fillBookWindow(aggBidsAll, spreadStep, 'bid', centerPx, LADDER_LEVELS) : []),
    [aggBidsAll, spreadStep, centerPx]
  );
  const asksAsc = useMemo(
    () => (askStart != null ? fillBookWindow(aggAsksAll, spreadStep, 'ask', askStart, LADDER_LEVELS) : []),
    [aggAsksAll, spreadStep, askStart]
  );
  const asks = useMemo(() => [...asksAsc].reverse(), [asksAsc]);
  const pinnedOff = liveCenter > 0 && centerPx > 0 && Math.abs(liveCenter - centerPx) / (spreadStep || 1) >= 2;

  const selectSizeUnit = (unit) => {
    setSizeUnit(unit);
    saveBookSizeUnit(unit);
  };

  const maxSz = useMemo(() => {
    let m = 0;
    for (const l of asks) if (l[1] > 0) m = Math.max(m, bookDisplaySz(l[1], l[0], sizeUnit) || 0);
    for (const l of bids) if (l[1] > 0) m = Math.max(m, bookDisplaySz(l[1], l[0], sizeUnit) || 0);
    return m || 1;
  }, [asks, bids, sizeUnit]);

  const bestBid = liveBid ?? bbo?.bid?.[0];
  const bestAsk = liveAsk ?? bbo?.ask?.[0];
  const mid = bestBid != null && bestAsk != null ? (bestBid + bestAsk) / 2 : rawMid;
  const spread = bestBid != null && bestAsk != null ? bestAsk - bestBid : null;
  const spreadBps = spread != null && mid ? (spread / mid) * 10000 : null;
  const bidTotal = bids.reduce((a, l) => a + l[1], 0);
  const askTotal = asks.reduce((a, l) => a + l[1], 0);
  const imbalance = bidTotal + askTotal > 0 ? (bidTotal - askTotal) / (bidTotal + askTotal) : 0;

  const orderPxs = useMemo(() => {
    const map = new Map();
    for (const o of openOrders || []) {
      const d = priceDecimals(o.price);
      map.set(Number(o.price).toFixed(d), o.side);
    }
    return map;
  }, [openOrders]);
  const entryKey = position?.entryPrice != null ? Number(position.entryPrice).toFixed(priceDecimals(position.entryPrice)) : null;

  const row = (lvl, side, cum, idx) => {
    const [px, sz, n] = lvl;
    const empty = !(sz > 0);
    const shown = empty ? 0 : bookDisplaySz(sz, px, sizeUnit) || 0;
    const pct = Math.min(100, (shown / maxSz) * 100);
    const key = px.toFixed(priceDecimals(spreadStep < 1 ? spreadStep : px));
    const hasOrder = orderPxs.get(Number(px).toFixed(priceDecimals(px)));
    const isEntry = entryKey === Number(px).toFixed(priceDecimals(px));
    const isBbo = side === 'ask' ? liveAsk != null && stepsEqual(px, liveAsk) : liveBid != null && stepsEqual(px, liveBid);
    return (
      <div
        role="button"
        tabIndex={0}
        key={`${side}-${idx}-${key}`}
        className={`dsc-lvl dsc-lvl-${side}${hasOrder ? ' has-order' : ''}${isEntry ? ' is-entry' : ''}${empty ? ' is-empty' : ''}${isBbo ? ' is-bbo' : ''}`}
        onClick={() => onPick(px, side)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(px, side); } }}
        title={empty
          ? `No ${side}s at ${fmtPx(px, spreadStep)} · click sets limit`
          : `${side === 'ask' ? 'Sell' : 'Buy'} · ${n || 1} orders · click sets limit @ ${fmtPx(px, spreadStep)}`}
      >
        <span className="dsc-lvl-bar" style={{ width: `${pct}%` }} />
        <span className="dsc-lvl-px">{fmtPx(px, spreadStep < 1 ? spreadStep : px)}</span>
        <span className="dsc-lvl-sz">{empty ? '—' : fmtBookSz(sz, px, sizeUnit)}</span>
        <span className="dsc-lvl-cum">{!empty && cum > 0 ? fmtBookSz(cum, px, sizeUnit) : '—'}</span>
        {hasOrder && <span className={`dsc-lvl-tag dsc-tag-${hasOrder}`}>{hasOrder === 'buy' ? 'B' : 'S'}</span>}
        {isEntry && <span className="dsc-lvl-tag dsc-tag-entry">E</span>}
      </div>
    );
  };

  let cum = 0;
  const askRows = [];
  const askCums = asksAsc.map((l) => (cum += l[1]));
  for (let i = asksAsc.length - 1; i >= 0; i--) askRows.push(row(asksAsc[i], 'ask', askCums[i], i));
  cum = 0;
  const bidRows = bids.map((l, i) => row(l, 'bid', (cum += l[1]), i));

  return (
    <div className="dsc-ladder">
      <div className="dsc-spread-step">
        <span className="dsc-spread-step-label">Spread</span>
        <div className="dsc-spread-step-options" role="group" aria-label="Spread step">
          {spreadSteps.map((step) => (
            <button
              type="button"
              key={step}
              className={`dsc-spread-step-btn${stepsEqual(spreadStep, step) ? ' active' : ''}`}
              onClick={() => selectSpreadStep(step)}
            >
              {spreadStepLabel(step)}
            </button>
          ))}
        </div>
      </div>
      <div className="dsc-spread-step dsc-size-unit">
        <span className="dsc-spread-step-label">Size</span>
        <div className="dsc-spread-step-options" role="group" aria-label="Order book size unit">
          <button
            type="button"
            className={`dsc-spread-step-btn${sizeUnit === 'usdc' ? ' active' : ''}`}
            onClick={() => selectSizeUnit('usdc')}
          >
            USDC
          </button>
          <button
            type="button"
            className={`dsc-spread-step-btn${sizeUnit === 'coin' ? ' active' : ''}`}
            onClick={() => selectSizeUnit('coin')}
          >
            {coinLabel}
          </button>
        </div>
      </div>
      <div className="dsc-ladder-head">
        <span>Price</span>
        <span>Size</span>
        <span>Total</span>
      </div>
      <div className="dsc-ladder-asks">{askRows.length ? askRows : <div className="dsc-ladder-empty">Waiting for book…</div>}</div>
      <div className={`dsc-ladder-mid ${flash || ''}`}>
        <div className="dsc-mid-px">{fmtPx(mid, mid)}</div>
        <div className="dsc-mid-meta">
          <span>
            Spread {spread != null ? fmtPx(spread, spreadStep < 1 ? spreadStep : 100) : '—'}
            {spreadBps != null ? ` · ${spreadBps.toFixed(2)} bps` : ''}
            {` · @ ${spreadStepLabel(spreadStep)}`}
          </span>
          <span>
            {pinnedOff ? (
              <button
                type="button"
                className="dsc-link dsc-recenter"
                onClick={() => liveCenter > 0 && setPinCenter({ px: liveCenter, step: spreadStep })}
              >
                Recenter
              </button>
            ) : null}
            <span className={imbalance >= 0 ? 'pos' : 'neg'}>Imb {(imbalance * 100).toFixed(0)}%</span>
          </span>
        </div>
      </div>
      <div className="dsc-ladder-bids">{bidRows}</div>
    </div>
  );
}

/* --------------------------------- Tape ---------------------------------- */

function Tape({ trades, mid }) {
  return (
    <div className="dsc-tape">
      <div className="dsc-panel-title">Time &amp; sales</div>
      <div className="dsc-tape-head">
        <span>Time</span>
        <span>Price</span>
        <span>Size</span>
      </div>
      <div className="dsc-tape-rows">
        {(trades || []).slice(0, 40).map((t, i) => (
          <div key={`${t.tid || i}-${t.t}`} className={`dsc-tape-row ${t.side === 'buy' ? 'buy' : 'sell'}`}>
            <span>{fmtTime(t.t)}</span>
            <span>{fmtPx(Number(t.px), mid)}</span>
            <span>{fmtSz(Number(t.sz))}</span>
          </div>
        ))}
        {!trades?.length && <div className="dsc-ladder-empty">No trades yet</div>}
      </div>
    </div>
  );
}

/* ------------------------------ Market picker ----------------------------- */

function MarketPicker({ current, onSelect, favorites, onToggleFavorite }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_BASE}/${API}/markets`, {
          headers: authHeaders(),
          params: { q, limit: 120 },
        });
        if (!cancelled) setRows(data.markets || []);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, open]);

  const pick = (m) => {
    onSelect({ symbol: m.symbol, coin: m.coin, dex: m.dex || 'main', displayName: m.displayName, category: m.category });
    setOpen(false);
    setQ('');
  };

  return (
    <div className="dsc-picker" ref={boxRef}>
      <button type="button" className="dsc-picker-btn" onClick={() => setOpen((o) => !o)}>
        <span className="dsc-picker-sym">{current.symbol}</span>
        <span className="dsc-picker-dex">{(current.dex || 'main').toLowerCase()}</span>
        <span className="dsc-picker-caret">▾</span>
      </button>
      <div className="dsc-quick">
        {[...favorites, ...QUICK_MARKETS.filter((m) => !favorites.some((f) => marketKey(f) === marketKey(m)))].slice(0, 8).map((m) => (
          <button
            type="button"
            key={marketKey(m)}
            className={`dsc-quick-btn${marketKey(m) === marketKey(current) ? ' active' : ''}`}
            onClick={() => onSelect(m)}
          >
            {m.symbol}
            {m.dex && m.dex !== 'main' ? <small>{m.dex}</small> : null}
          </button>
        ))}
        <button
          type="button"
          className={`dsc-quick-btn dsc-fav${favorites.some((f) => marketKey(f) === marketKey(current)) ? ' on' : ''}`}
          title="Pin / unpin this market"
          onClick={() => onToggleFavorite(current)}
        >
          ★
        </button>
      </div>
      {open && (
        <div className="dsc-picker-drop">
          <input
            autoFocus
            placeholder="Search BTC, ETH, TSLA, GOLD…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="dsc-picker-list">
            {loading && !rows.length && <div className="dsc-ladder-empty">Loading…</div>}
            {rows.map((m) => (
              <button type="button" key={marketKey(m)} className="dsc-picker-row" onClick={() => pick(m)}>
                <span className="sym">{m.symbol}</span>
                <span className={`dex dex-${(m.dex || 'main').toLowerCase()}`}>{(m.dex || 'main').toLowerCase()}</span>
                <span className="cat">{m.category}</span>
                <span className="mid">{m.mid != null ? fmtPx(m.mid, m.mid) : ''}</span>
              </button>
            ))}
            {!loading && !rows.length && <div className="dsc-ladder-empty">No markets match</div>}
          </div>
        </div>
      )}
    </div>
  );
}

/* --------------------------------- Page ----------------------------------- */

export default function DiscretionaryScalping() {
  const { symbol: routeSymbol } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [access, setAccess] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [market, setMarket] = useState(() => ({
    symbol: (routeSymbol || 'BTC').toUpperCase(),
    coin: routeSymbol ? null : 'BTC',
    dex: (searchParams.get('dex') || 'main').toLowerCase(),
  }));
  const [meta, setMeta] = useState(null);
  const [account, setAccount] = useState(null);
  const [acctErr, setAcctErr] = useState(null);
  const [walletOpen, setWalletOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [showChart, setShowChart] = useState(true);
  const [tab, setTab] = useState('positions');
  const [chatUnread, setChatUnread] = useState(0);
  const [exporting, setExporting] = useState(false);

  // ticket
  const [orderType, setOrderType] = useState('market');
  const [sizeMode, setSizeMode] = useState('usd');
  const [sizeUsd, setSizeUsd] = useState('100');
  const [sizeContracts, setSizeContracts] = useState('');
  const [price, setPrice] = useState('');
  const [leverage, setLeverage] = useState(5);
  const [reduceOnly, setReduceOnly] = useState(false);
  const [slippageBps, setSlippageBps] = useState(20);
  const [quote, setQuote] = useState(null);

  const coinKey = market.coin || market.symbol;
  const feedCoin = market.dex && market.dex !== 'main' && !String(coinKey).includes(':') ? `${market.dex}:${market.symbol}` : coinKey;
  const stream = useMarketStream(feedCoin, market.dex, { enabled: !!access?.allowed });
  const { book, bbo, ctx, trades, status: feedStatus, latencyMs } = stream;

  const bestBid = bbo?.bid?.[0] ?? book?.bids?.[0]?.[0] ?? null;
  const bestAsk = bbo?.ask?.[0] ?? book?.asks?.[0]?.[0] ?? null;
  const mid = bestBid != null && bestAsk != null ? (bestBid + bestAsk) / 2 : ctx?.midPx ?? null;

  const lastMidRef = useRef(null);
  const [flash, setFlash] = useState('');
  useEffect(() => {
    if (mid == null) return;
    if (lastMidRef.current != null && mid !== lastMidRef.current) {
      setFlash(mid > lastMidRef.current ? 'up' : 'down');
      const t = setTimeout(() => setFlash(''), 220);
      lastMidRef.current = mid;
      return () => clearTimeout(t);
    }
    lastMidRef.current = mid;
    return undefined;
  }, [mid]);

  const showToast = useCallback((kind, text) => {
    setToast({ kind, text, id: Date.now() });
  }, []);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 4500);
    return () => clearTimeout(t);
  }, [toast]);

  /* ---- access + config ---- */
  const loadConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/${API}/config`, { headers: authHeaders() });
      setCfg(data);
      setSizeUsd((s) => (s === '100' && data.defaultSizeUsd ? String(data.defaultSizeUsd) : s));
      setLeverage((l) => (l === 5 && data.defaultLeverage ? data.defaultLeverage : l));
      setSlippageBps(data.defaultSlippageBps ?? 20);
    } catch (e) {
      showToast('err', e.response?.data?.error || e.message);
    }
  }, [showToast]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/${API}/access`, { headers: authHeaders() });
        if (cancelled) return;
        setAccess(data);
        if (data.allowed) loadConfig();
      } catch (e) {
        if (!cancelled) setAccess({ allowed: false, error: e.response?.data?.error || e.message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadConfig]);

  /* ---- market meta ---- */
  useEffect(() => {
    if (!access?.allowed) return undefined;
    let cancelled = false;
    setMeta(null);
    axios
      .get(`${API_BASE}/${API}/market-meta/${market.symbol}`, { headers: authHeaders(), params: { dex: market.dex } })
      .then(({ data }) => {
        if (cancelled) return;
        setMeta(data);
        if (!market.coin || market.coin !== data.coin) setMarket((m) => ({ ...m, coin: data.coin, dex: data.dex || m.dex }));
        setLeverage((l) => Math.min(l, data.maxLeverage || l));
      })
      .catch((e) => {
        if (!cancelled) showToast('err', e.response?.data?.error || `Market ${market.symbol} not found`);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market.symbol, market.dex, access?.allowed]);

  const selectMarket = useCallback(
    (m) => {
      const dex = (m.dex || 'main').toLowerCase();
      setMarket({ symbol: m.symbol.toUpperCase(), coin: m.coin || null, dex });
      setPrice('');
      setQuote(null);
      navigate(`/discretionary-scalping/${m.symbol.toUpperCase()}${dex !== 'main' ? `?dex=${dex}` : ''}`, { replace: true });
    },
    [navigate]
  );

  /* ---- account polling ---- */
  const acctTick = useRef(0);
  const loadAccount = useCallback(
    async (full = false) => {
      if (!access?.allowed) return;
      try {
        const { data } = await axios.get(`${API_BASE}/${API}/account`, {
          headers: authHeaders(),
          params: { dex: market.dex, light: full ? undefined : 1 },
          timeout: 15000,
        });
        setAccount((prev) => {
          if (!full && prev?.balances && data.balances && !data.paperMode) {
            return {
              ...data,
              balances: mergeLiveBalances(prev.balances, data.balances),
              fees: data.fees ?? prev.fees,
            };
          }
          return data;
        });
        setAcctErr(null);
      } catch (e) {
        setAcctErr(e.response?.data?.error || e.message);
      }
    },
    [access?.allowed, market.dex]
  );

  useEffect(() => {
    if (!access?.allowed) return undefined;
    loadAccount(true);
    const t = setInterval(() => {
      acctTick.current += 1;
      loadAccount(acctTick.current % Math.round(ACCOUNT_FULL_MS / ACCOUNT_LIGHT_MS) === 0);
    }, ACCOUNT_LIGHT_MS);
    return () => clearInterval(t);
  }, [loadAccount, access?.allowed]);

  const refreshSoon = useCallback(() => {
    setTimeout(() => loadAccount(true), 400);
    setTimeout(() => loadAccount(false), 2000);
  }, [loadAccount]);

  /* ---- ticket helpers ---- */
  const paperMode = account?.paperMode ?? cfg?.paperMode ?? true;
  const needsPrice = orderType === 'limit' || orderType === 'post_only';
  const liveMark = ctx?.markPx ?? mid;
  const positions = useMemo(
    () =>
      (account?.positions || []).map((p) =>
        enrichPosition(p, { mark: liveMark, coin: meta?.coin || market.coin, symbol: market.symbol })
      ),
    [account?.positions, liveMark, meta?.coin, market.coin, market.symbol]
  );
  const position = useMemo(
    () => positions.find((p) => p.coin === (meta?.coin || market.coin) || p.symbol === market.symbol) || null,
    [positions, meta?.coin, market.coin, market.symbol]
  );
  const openOrdersHere = useMemo(
    () => (account?.openOrders || []).filter((o) => o.coin === (meta?.coin || market.coin) || o.symbol === market.symbol),
    [account?.openOrders, meta?.coin, market.coin, market.symbol]
  );

  const ticketBody = useCallback(
    (side) => {
      const body = {
        symbol: market.symbol,
        coin: meta?.coin || market.coin || market.symbol,
        dex: market.dex,
        side,
        orderType,
        reduceOnly,
        leverage,
        slippageBps,
      };
      if (sizeMode === 'usd') body.sizeUsd = Number(sizeUsd);
      else body.sizeContracts = Number(sizeContracts);
      if (needsPrice || (orderType === 'ioc' && price)) body.price = Number(price);
      return body;
    },
    [market, meta?.coin, orderType, reduceOnly, leverage, slippageBps, sizeMode, sizeUsd, sizeContracts, needsPrice, price]
  );

  // fee quote (debounced) — uses buy side; commission bps may differ per side so we show both bps from config
  useEffect(() => {
    if (!access?.allowed || !meta) return undefined;
    const body = ticketBody('buy');
    if (sizeMode === 'usd' ? !(body.sizeUsd > 0) : !(body.sizeContracts > 0)) {
      setQuote(null);
      return undefined;
    }
    if (needsPrice && !(body.price > 0)) {
      setQuote(null);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API_BASE}/${API}/quote`, body, { headers: authHeaders() });
        if (!cancelled) setQuote(data);
      } catch (e) {
        if (!cancelled) setQuote({ error: e.response?.data?.error || e.message });
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [ticketBody, access?.allowed, meta, sizeMode, needsPrice]);

  const submit = async (side) => {
    if (busy) return;
    const body = ticketBody(side);
    if (needsPrice && !(body.price > 0)) return showToast('err', 'Enter a limit price (or click a ladder level).');
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE}/${API}/order`, body, { headers: authHeaders(), timeout: 30000 });
      const st = data.status || (data.filled > 0 ? 'filled' : 'submitted');
      const px = data.avgPx ?? data.fillPrice ?? data.restingPx ?? data.price ?? data.intent?.price;
      const isResting = st === 'resting' || st === 'paper_resting';
      if (isResting) setTab('orders');
      showToast(
        'ok',
        isResting
          ? `Limit ${side.toUpperCase()} ${data.intent?.contracts ?? ''} ${market.symbol} is working @ ${fmtPx(Number(px), mid)}${data.paper ? ' (paper)' : ''} — sits on the book until the market trades there (or you cancel)`
          : `${side.toUpperCase()} ${data.filled ?? data.filledContracts ?? data.intent?.contracts ?? ''} ${market.symbol} filled @ ${fmtPx(Number(px), mid)}${data.paper ? ' (paper)' : ''}`
      );
      refreshSoon();
    } catch (e) {
      const code = e.response?.data?.code;
      showToast('err', e.response?.data?.error || e.message);
      if (code === 'no_wallet' || code === 'agent_required') setWalletOpen(true);
    } finally {
      setBusy(false);
    }
  };

  const doPost = async (path, body, okMsg) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE}/${API}/${path}`, body || {}, { headers: authHeaders(), timeout: 30000 });
      showToast(data.success === false ? 'err' : 'ok', typeof okMsg === 'function' ? okMsg(data) : okMsg);
      refreshSoon();
    } catch (e) {
      showToast('err', e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const sameMarketAsTicket = (p) =>
    !!p && (p.coin === (meta?.coin || market.coin) || p.symbol === market.symbol);

  const closeLimitPxFor = (p) => {
    const typed = Number(price);
    if (typed > 0) return typed;
    if (p?.side === 'long') return bestAsk ?? mid;
    if (p?.side === 'short') return bestBid ?? mid;
    return mid;
  };

  const closePosition = (p) => {
    const same = sameMarketAsTicket(p);
    const wantLimit = same && (orderType === 'limit' || orderType === 'post_only');
    const px = closeLimitPxFor(p);
    if (wantLimit && !(px > 0)) {
      return showToast('err', 'Enter a limit price (or click a ladder level) to close with a limit.');
    }
    const body = {
      coin: p.coin,
      dex: p.dex,
      symbol: p.symbol,
      slippageBps: Math.max(slippageBps, 30),
    };
    if (wantLimit) {
      body.orderType = orderType;
      body.price = px;
    }
    doPost('close-position', body, (d) => {
      const st = d.status;
      const isResting = st === 'resting' || st === 'paper_resting';
      const fillPx = d.avgPx ?? d.restingPx ?? d.price ?? px;
      if (isResting) setTab('orders');
      return isResting
        ? `Limit ${p.side === 'long' ? 'SELL' : 'BUY'} ${p.symbol} is working @ ${fmtPx(Number(fillPx), mid)}${d.paper ? ' (paper)' : ''} — sits on the book until the market trades there`
        : `Closed ${p.symbol} ${d.filled ?? ''} @ ${fmtPx(Number(d.avgPx), mid)}`;
    });
  };
  const cancelOrder = (o) => doPost('cancel', { oid: o.oid, coin: o.coin, dex: o.dex, orderId: o.orderId }, `Cancelled ${o.symbol || o.coin} ${o.side} @ ${fmtPx(Number(o.price), mid)}`);
  const cancelAll = () => doPost('cancel-all', { coin: meta?.coin || market.coin, dex: market.dex }, (d) => `Cancelled ${d.cancelled ?? 0} order(s)`);
  const flatten = () => {
    if (
      !window.confirm(
        `Flatten Disc Scalping positions and cancel Disc Scalping open orders${paperMode ? ' (paper)' : ' — LIVE'}? Other desks on this wallet are left alone.`
      )
    )
      return;
    doPost('flatten', {}, (d) => `Flattened: ${d.closed?.length || 0} closed, ${d.cancelled || 0} cancelled${d.errors?.length ? ` · ${d.errors.join('; ')}` : ''}`);
  };

  const exportReport = async (kind) => {
    if (exporting) return;
    setExporting(true);
    try {
      const { data } = await axios.get(`${API_BASE}/${API}/report`, {
        headers: authHeaders(),
        params: { dex: market.dex },
        timeout: 30000,
      });
      if (kind === 'pdf') downloadDiscScalpingPdf(data);
      else downloadDiscScalpingCsv(data);
      showToast('ok', kind === 'pdf' ? 'PDF report saved' : 'CSV report saved');
    } catch (e) {
      showToast('err', e.response?.data?.error || e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const togglePaper = async () => {
    const next = !paperMode;
    if (!next && !window.confirm('Switch to LIVE trading? Orders will be sent to Hyperliquid with real funds.')) return;
    try {
      const { data } = await axios.patch(`${API_BASE}/${API}/user-config`, { paperMode: next }, { headers: authHeaders() });
      setCfg(data);
      showToast('ok', next ? 'Paper mode on' : 'LIVE mode on');
      refreshSoon();
    } catch (e) {
      showToast('err', e.response?.data?.error || e.message);
    }
  };

  const favorites = cfg?.favorites || [];
  const toggleFavorite = async (m) => {
    const key = marketKey(m);
    const next = favorites.some((f) => marketKey(f) === key)
      ? favorites.filter((f) => marketKey(f) !== key)
      : [...favorites, { symbol: m.symbol, coin: meta?.coin || m.coin, dex: m.dex || 'main' }];
    try {
      const { data } = await axios.patch(`${API_BASE}/${API}/user-config`, { favorites: next }, { headers: authHeaders() });
      setCfg(data);
    } catch (e) {
      showToast('err', e.response?.data?.error || e.message);
    }
  };

  const pickLadderPrice = (px, side) => {
    setPrice(String(px));
    if (orderType === 'market') setOrderType('limit');
    if (sizeMode === 'contracts' && !sizeContracts && position) setSizeContracts(String(position.sizeContracts));
    void side;
  };

  const suggestedLimitPx = () => {
    if (position?.side === 'long') return bestAsk ?? mid;
    if (position?.side === 'short') return bestBid ?? mid;
    return mid;
  };

  const chooseOrderType = (id) => {
    setOrderType(id);
    if ((id === 'limit' || id === 'post_only') && !(Number(price) > 0)) {
      const px = suggestedLimitPx();
      if (px > 0) setPrice(String(px));
    }
  };

  const applyPositionPct = (pct) => {
    if (!position) return;
    if (sizeMode === 'usd') {
      const v = Math.abs(Number(position.positionValue) || 0) * (pct / 100);
      if (v > 0) setSizeUsd(String(Number(v.toFixed(2))));
    } else {
      setSizeContracts(String(Number((position.sizeContracts * pct) / 100).toPrecision(6)));
    }
  };

  const sizePresets = useMemo(() => {
    const base = Number(cfg?.defaultSizeUsd) || 100;
    return [base, base * 2.5, base * 5, base * 10].map((v) => Math.round(v));
  }, [cfg?.defaultSizeUsd]);

  /* ---- desk chat: a signal card prefills the ticket (market, limit @ entry); trader still sizes + confirms ---- */
  const loadSignalIntoTicket = useCallback(
    (sig) => {
      if (!sig) return;
      const dex = (sig.dex || 'main').toLowerCase();
      if (sig.symbol !== market.symbol || dex !== market.dex) selectMarket({ symbol: sig.symbol, coin: sig.coin || null, dex });
      if (sig.entry != null) {
        setOrderType('limit');
        setPrice(String(sig.entry));
      } else {
        setOrderType('market');
      }
      setReduceOnly(false);
      showToast('ok', `Signal loaded: ${String(sig.side).toUpperCase()} ${sig.symbol}${sig.entry != null ? ` @ ${sig.entry}` : ''} — set size, then ${sig.side === 'long' ? 'BUY' : 'SELL'}`);
    },
    [market.symbol, market.dex, selectMarket, showToast]
  );

  /* -------------------------------- render -------------------------------- */

  if (access && !access.allowed) {
    return (
      <div className="dsc-page dsc-gate">
        <p className="dsc-back"><Link to="/trading">← Back to Trading</Link></p>
        <h1>Discretionary Scalping</h1>
        <div className="dsc-gate-card">
          <p>{access.error || 'This desk is currently limited to admins and invited traders.'}</p>
          <p className="dsc-muted">Ask an admin to add your account under Admin → Disc. Scalping.</p>
        </div>
      </div>
    );
  }

  const bal = account?.balances || {};
  const equity = paperMode
    ? bal.equityUsd
    : firstPositiveUsd(bal.equityUsd, bal.spotUsdcTotal, bal.accountValueUsd, bal.withdrawableUsd, bal.spotUsdc);
  const withdrawable = paperMode
    ? bal.withdrawableUsd
    : firstPositiveUsd(bal.withdrawableUsd, bal.spotUsdc, bal.perpsWithdrawableUsd);
  const upnlTotal = positions.reduce((a, p) => a + (finiteNum(p.unrealizedPnl) || 0), 0);
  const realizedTotal = finiteNum(bal.deskRealizedPnlUsd ?? bal.realizedPnlUsd) || 0;
  const headerPnl = upnlTotal + realizedTotal;
  const commission = cfg?.commission;
  const ticketPx = Number(price) > 0 ? Number(price) : mid;
  const ticketNotional =
    sizeMode === 'usd'
      ? Number(sizeUsd) || 0
      : (Number(sizeContracts) || 0) * (ticketPx || 0);
  const hlTakerBps = Number(commission?.hlTakerFeeBps) >= 4 ? Number(commission.hlTakerFeeBps) : 4.5;
  const hlMakerBps = Number(commission?.hlMakerFeeBps) > 0 ? Number(commission.hlMakerFeeBps) : 1.5;
  const hlBps = orderType === 'post_only' ? hlMakerBps : hlTakerBps;
  const estCommissionUsd = (side) => {
    if (!(ticketNotional > 0)) return 0;
    const omniBps =
      commission?.enabled
        ? side === 'sell'
          ? Number(commission.bpsSell) || 0
          : Number(commission.bpsBuy) || 0
        : 0;
    const raw = (ticketNotional * (hlBps + omniBps)) / 10000;
    if (!(raw > 0)) return 0;
    if (raw < 0.01) return 0.01;
    return Math.round(raw * 100) / 100;
  };
  const estBuy = estCommissionUsd('buy');
  const estSell = estCommissionUsd('sell');
  const feeLine =
    ticketNotional > 0
      ? estBuy === estSell
        ? `Est. commission ${fmtUsd(estBuy)}`
        : `Est. commission buy ${fmtUsd(estBuy)} · sell ${fmtUsd(estSell)}`
      : null;

  return (
    <div className="dsc-page">
      <header className="dsc-top">
        <div className="dsc-brand-row">
          <Link to="/dashboard" className="dsc-brand" aria-label="Omni Scalping home">
            <img src={omniMobileLogo} alt="Omni Scalping" className="dsc-brand-logo" />
          </Link>
          <a
            className="dsc-discord-top"
            href="https://discord.gg/y67qMBA59"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open Discord"
            title="Open Discord"
          >
            <svg viewBox="0 0 127.14 96.36" width="20" height="20" aria-hidden="true">
              <path
                fill="currentColor"
                d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z"
              />
            </svg>
          </a>
        </div>
        <div className="dsc-top-left">
          <Link to="/trading" className="dsc-back">← Trading</Link>
          <h1>
            Discretionary Scalping <span className="dsc-badge">Hyperliquid</span>
          </h1>
        </div>
        <MarketPicker current={market} onSelect={selectMarket} favorites={favorites} onToggleFavorite={toggleFavorite} />
        <div className="dsc-top-right">
          <button type="button" className={`dsc-mode ${paperMode ? 'paper' : 'live'}`} onClick={togglePaper} title="Toggle paper / live">
            {paperMode ? 'PAPER' : 'LIVE'}
          </button>
          <span className={`dsc-feed dsc-feed-${feedStatus}`} title={`Feed: ${feedStatus}${stream.error ? ` — ${stream.error}` : ''}`}>
            <i />
            {feedStatus === 'live' ? `${latencyMs != null ? `${Math.min(latencyMs, 9999)} ms` : 'live'}` : feedStatus}
          </span>
          <button type="button" className={`dsc-wallet-pill${account?.walletAddress ? '' : ' missing'}`} onClick={() => setWalletOpen(true)}>
            {account?.walletAddress
              ? `${account.walletAddress.slice(0, 6)}…${account.walletAddress.slice(-4)}${account.agentApproved ? '' : ' · agent?'}`
              : 'Link HL wallet'}
          </button>
          <PageGearButton onClick={() => setWalletOpen(true)} label="HL wallet & agent" icon="wallet" />
        </div>
      </header>

      <div className="dsc-stats">
        <div><span>Mark</span><strong className={flash}>{fmtPx(ctx?.markPx ?? mid, mid)}</strong></div>
        <div><span>24h</span><strong className={ctx?.prevDayPx && mid >= ctx.prevDayPx ? 'pos' : 'neg'}>
          {ctx?.prevDayPx && mid ? `${(((mid - ctx.prevDayPx) / ctx.prevDayPx) * 100).toFixed(2)}%` : '—'}
        </strong></div>
        <div><span>Funding</span><strong>{ctx?.funding != null ? `${(ctx.funding * 100).toFixed(4)}%` : '—'}</strong></div>
        <div><span>OI</span><strong>{ctx?.openInterest != null && mid ? fmtCompactUsd(ctx.openInterest * mid) : '—'}</strong></div>
        <div><span>24h Vol</span><strong>{fmtCompactUsd(ctx?.dayNtlVlm)}</strong></div>
        <div className="dsc-stats-sep" />
        <div><span>{paperMode ? 'Paper equity' : 'Wallet'}</span><strong>{fmtUsd(equity)}</strong></div>
        <div><span>Available</span><strong>{fmtUsd(withdrawable)}</strong></div>
        <div className="dsc-stats-pnl" title="Disc realized + open unrealized (this desk only)">
          <span>PNL</span>
          <strong className={headerPnl >= 0 ? 'pos' : 'neg'}>{fmtPnl(headerPnl)}</strong>
        </div>
        {account?.commissionOwedUsd > 0 && <div><span>Comm. owed</span><strong className="neg">{fmtUsd(account.commissionOwedUsd)}</strong></div>}
        <div className="dsc-stats-fees" title="Omni commission on this ticket (updates as you change size)">
          <span>Commission</span>
          <strong>{feeLine || '—'}</strong>
        </div>
      </div>

      {!paperMode && account && (!account.walletAddress || !account.agentApproved) && (
        <div className="dsc-banner warn">
          {!account.walletAddress
            ? 'Link your Hyperliquid wallet to trade live.'
            : account.agentStatus?.revoked
              ? account.agentStatus.message ||
                'Trading agent is no longer approved on Hyperliquid. Open HL Wallet and tap Approve agent again.'
              : 'Approve the trading agent (one signature) so orders can be sent instantly without MetaMask prompts.'}
          <button type="button" onClick={() => setWalletOpen(true)}>Open HL wallet</button>
        </div>
      )}
      {account?.hlError && !paperMode && <div className="dsc-banner err">Hyperliquid: {account.hlError}</div>}
      {acctErr && <div className="dsc-banner err">Account: {acctErr}</div>}

      <div className={`dsc-grid${showChart ? '' : ' no-chart'}`}>
        <section className="dsc-col dsc-col-book">
          <div className="dsc-panel-title">
            Order book <small>{meta?.coin || feedCoin}</small>
          </div>
          <Ladder
            book={book}
            bbo={bbo}
            ctx={ctx}
            position={position}
            openOrders={openOrdersHere}
            onPick={pickLadderPrice}
            flash={flash}
            coin={meta?.coin || market.coin || market.symbol || feedCoin}
            dex={meta?.dex || market.dex || 'main'}
            symbol={market.symbol}
          />
        </section>

        <section className="dsc-col dsc-col-ticket">
          <div className="dsc-ticket">
            <div className="dsc-otypes">
              {ORDER_TYPES.map((t) => (
                <button
                  type="button"
                  key={t.id}
                  className={orderType === t.id ? 'active' : ''}
                  title={t.hint}
                  onClick={() => chooseOrderType(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="dsc-field-row">
              <label>
                <span>Size</span>
                <div className="dsc-size-input">
                  {sizeMode === 'usd' ? (
                    <input type="number" min="0" step="10" value={sizeUsd} onChange={(e) => setSizeUsd(e.target.value)} />
                  ) : (
                    <input type="number" min="0" step="any" value={sizeContracts} onChange={(e) => setSizeContracts(e.target.value)} placeholder={position ? String(position.sizeContracts) : '0'} />
                  )}
                  <button type="button" className="dsc-unit" onClick={() => setSizeMode((m) => (m === 'usd' ? 'contracts' : 'usd'))}>
                    {sizeMode === 'usd' ? 'USD' : market.symbol}
                  </button>
                </div>
              </label>
            </div>
            <div className="dsc-presets">
              {sizeMode === 'usd' &&
                sizePresets.map((v) => (
                  <button type="button" key={v} className={String(v) === sizeUsd ? 'active' : ''} onClick={() => setSizeUsd(String(v))}>
                    ${v >= 1000 ? `${v / 1000}k` : v}
                  </button>
                ))}
              {(sizeMode === 'contracts' || position) &&
                [25, 50, 75, 100].map((pct) => (
                  <button type="button" key={pct} disabled={!position} onClick={() => applyPositionPct(pct)}>
                    {pct}%
                  </button>
                ))}
            </div>

            {(needsPrice || orderType === 'ioc') && (
              <div className="dsc-field-row">
                <label>
                  <span>Price {orderType === 'ioc' ? '(optional)' : ''}</span>
                  <div className="dsc-size-input">
                    <input type="number" min="0" step="any" value={price} onChange={(e) => setPrice(e.target.value)} placeholder={mid ? fmtPx(mid, mid).replace(/,/g, '') : ''} />
                    <button type="button" className="dsc-unit" title="Best bid" onClick={() => bestBid != null && setPrice(String(bestBid))}>Bid</button>
                    <button type="button" className="dsc-unit" title="Best ask" onClick={() => bestAsk != null && setPrice(String(bestAsk))}>Ask</button>
                  </div>
                </label>
              </div>
            )}

            <div className="dsc-field-row dsc-two">
              <label>
                <span>Leverage {meta?.maxLeverage ? `(max ${meta.maxLeverage}x)` : ''}</span>
                <div className="dsc-lev">
                  <input type="range" min="1" max={meta?.maxLeverage || cfg?.maxLeverageCap || 50} value={leverage} onChange={(e) => setLeverage(Number(e.target.value))} />
                  <input type="number" min="1" max={meta?.maxLeverage || 50} value={leverage} onChange={(e) => setLeverage(Math.max(1, Math.min(meta?.maxLeverage || 50, Number(e.target.value) || 1)))} />
                  <b>x</b>
                </div>
              </label>
              {(orderType === 'market' || orderType === 'ioc') && (
                <label className="dsc-slip">
                  <span>Slippage</span>
                  <div className="dsc-lev">
                    <input type="number" min="0" max="500" value={slippageBps} onChange={(e) => setSlippageBps(Number(e.target.value) || 0)} />
                    <b>bps</b>
                  </div>
                </label>
              )}
            </div>

            <label className="dsc-check">
              <input type="checkbox" checked={reduceOnly} onChange={(e) => setReduceOnly(e.target.checked)} />
              Reduce-only
            </label>
            {position ? (
              <p className="dsc-ro-hint">
                {position.side === 'long'
                  ? 'To exit the long with a limit: Limit (or click the book) → check Reduce-only → SELL / Close. Or use Limit close below.'
                  : 'To cover the short with a limit: Limit (or click the book) → check Reduce-only → BUY / Cover. Or use Limit close below.'}
              </p>
            ) : null}

            <div className="dsc-quote">
              {quote?.error ? (
                <span className="neg">{quote.error}</span>
              ) : (
                <>
                  <div>
                    <span>
                      {quote
                        ? `${quote.contracts} ${market.symbol}`
                        : ticketNotional > 0
                          ? '…'
                          : '—'}
                    </span>
                    <span>
                      {quote
                        ? `≈ ${fmtUsd(quote.notionalUsd)} · margin ${fmtUsd(quote.marginUsd)}`
                        : ticketNotional > 0
                          ? `≈ ${fmtUsd(ticketNotional)}`
                          : 'Enter a size'}
                    </span>
                  </div>
                  <div className="dsc-quote-fees">
                    {feeLine || 'Enter a size to see commission'}
                  </div>
                </>
              )}
            </div>

            <div className="dsc-sides">
              <button type="button" className="dsc-buy" disabled={busy || !meta} onClick={() => submit('buy')}>
                <b>{reduceOnly || position?.side === 'short' ? 'BUY / Cover' : 'BUY / Long'}</b>
                <small>{bestAsk != null ? fmtPx(bestAsk, mid) : '—'}</small>
              </button>
              <button type="button" className="dsc-sell" disabled={busy || !meta} onClick={() => submit('sell')}>
                <b>{reduceOnly || position?.side === 'long' ? 'SELL / Close' : 'SELL / Short'}</b>
                <small>{bestBid != null ? fmtPx(bestBid, mid) : '—'}</small>
              </button>
            </div>

            <div className="dsc-actions">
              <button
                type="button"
                disabled={busy || !position}
                title={
                  position && (orderType === 'limit' || orderType === 'post_only')
                    ? `Reduce-only ${orderType} ${position.side === 'long' ? 'sell' : 'buy'} @ ${fmtPx(closeLimitPxFor(position), mid)}`
                    : 'Market close (reduce-only IOC)'
                }
                onClick={() => position && closePosition(position)}
              >
                {position && (orderType === 'limit' || orderType === 'post_only')
                  ? `Limit close @ ${fmtPx(closeLimitPxFor(position), mid)}`
                  : `Mkt close ${market.symbol}`}
              </button>
              <button type="button" disabled={busy || !openOrdersHere.length} onClick={cancelAll}>
                Cancel all ({openOrdersHere.length})
              </button>
              <button type="button" className="danger" disabled={busy || (!account?.positions?.length && !account?.openOrders?.length)} onClick={flatten}>
                Flatten all
              </button>
            </div>

            {position && (
              <div className={`dsc-pos-strip ${position.side}`}>
                <span>
                  {position.side.toUpperCase()} {fmtSz(position.sizeContracts)} @ {fmtPx(position.entryPrice, mid)}
                </span>
                <span className={`dsc-pnl-chip ${finiteNum(position.unrealizedPnl) >= 0 ? 'pos' : 'neg'}`}>
                  {fmtPnlRoe(position.unrealizedPnl, roeOf(position))}
                </span>
              </div>
            )}
          </div>
          <Tape trades={trades} mid={mid} />
        </section>

        <section className="dsc-col dsc-col-main">
          <div className="dsc-chart-wrap">
            <div className="dsc-panel-title dsc-chart-title">
              Chart
              <button type="button" className="dsc-link" onClick={() => setShowChart((s) => !s)}>{showChart ? 'hide' : 'show'}</button>
            </div>
            {showChart && meta && (
              <HlTradingChart
                symbol={market.symbol}
                hlDex={market.dex}
                hlCoin={meta.coin}
                height={360}
                apiPrefix={API}
                showZoomButtons
              />
            )}
          </div>

          <div className="dsc-bottom">
            <div className="dsc-tabs">
              <button type="button" className={tab === 'positions' ? 'active' : ''} onClick={() => setTab('positions')}>
                Positions ({positions.length})
              </button>
              <button type="button" className={tab === 'orders' ? 'active' : ''} onClick={() => setTab('orders')}>
                Open orders ({account?.openOrders?.length || 0})
              </button>
              <button type="button" className={tab === 'history' ? 'active' : ''} onClick={() => setTab('history')}>
                History
              </button>
              <button type="button" className={`dsc-tab-chat${tab === 'chat' ? ' active' : ''}`} onClick={() => setTab('chat')}>
                Desk chat{chatUnread > 0 && <b className="dsc-tab-badge">{chatUnread > 99 ? '99+' : chatUnread}</b>}
              </button>
              <div className="dsc-export">
                <button type="button" className="dsc-export-btn" disabled={exporting} onClick={() => exportReport('csv')}>
                  {exporting ? '…' : 'CSV'}
                </button>
                <button type="button" className="dsc-export-btn" disabled={exporting} onClick={() => exportReport('pdf')}>
                  {exporting ? '…' : 'PDF'}
                </button>
              </div>
              {paperMode && (
                <button
                  type="button"
                  className="dsc-link right"
                  onClick={() => window.confirm('Reset paper account to $10,000 and clear paper positions?') && doPost('paper/reset', {}, 'Paper account reset')}
                >
                  reset paper
                </button>
              )}
            </div>

            {tab === 'positions' && (
              <>
                {!paperMode && (
                  <p className="dsc-history-note">Disc Scalping positions only — not Omni, MAP, or other desks.</p>
                )}
                <div className="dsc-pos-cards">
                  {positions.map((p) => {
                    const pnl = finiteNum(p.unrealizedPnl);
                    const roe = roeOf(p);
                    return (
                      <article key={`${p.dex}:${p.coin}`} className={`dsc-pos-card ${p.side}`}>
                        <div className="dsc-pos-card-top">
                          <button type="button" className="dsc-link" onClick={() => selectMarket(p)}>
                            {p.symbol}
                            {p.leverage ? <small> {p.leverage}x</small> : null}
                            {p.dex !== 'main' ? <small> {p.dex}</small> : null}
                          </button>
                          <span className={`dsc-pnl-chip ${pnl >= 0 ? 'pos' : 'neg'}`}>{fmtPnlRoe(pnl, roe)}</span>
                        </div>
                        <div className="dsc-pos-card-grid">
                          <div>
                            <span>Size</span>
                            <strong className={p.side === 'long' ? 'pos' : 'neg'}>
                              {p.side === 'long' ? '' : '−'}
                              {fmtSz(p.sizeContracts)}
                            </strong>
                          </div>
                          <div>
                            <span>Value</span>
                            <strong>{fmtUsd(p.positionValue)}</strong>
                          </div>
                          <div>
                            <span>Entry</span>
                            <strong>{fmtPx(p.entryPrice, p.entryPrice)}</strong>
                          </div>
                          <div>
                            <span>Mark</span>
                            <strong>{fmtPx(p.markPrice, p.entryPrice)}</strong>
                          </div>
                          <div>
                            <span>Liq</span>
                            <strong>{p.liquidationPx ? fmtPx(p.liquidationPx, p.entryPrice) : '—'}</strong>
                          </div>
                          <div>
                            <span>Margin</span>
                            <strong>{p.marginUsed != null ? fmtUsd(p.marginUsed) : '—'}</strong>
                          </div>
                        </div>
                        <button type="button" className="dsc-mini dsc-pos-card-close" disabled={busy} onClick={() => closePosition(p)}>
                          {sameMarketAsTicket(p) && (orderType === 'limit' || orderType === 'post_only')
                            ? 'Limit close'
                            : 'Close'}
                        </button>
                      </article>
                    );
                  })}
                  {!positions.length && (
                    <p className="dsc-muted dsc-empty">No Disc Scalping positions</p>
                  )}
                </div>
                <div className="dsc-table-scroll dsc-table-desktop">
                  <table className="dsc-table">
                    <thead>
                      <tr>
                        <th>Market</th>
                        <th>Size</th>
                        <th>Position Value</th>
                        <th>Entry Price</th>
                        <th>Mark Price</th>
                        <th>PNL (ROE %)</th>
                        <th>Liq. Price</th>
                        <th>Margin</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {positions.map((p) => {
                        const pnl = finiteNum(p.unrealizedPnl);
                        const roe = roeOf(p);
                        return (
                          <tr key={`${p.dex}:${p.coin}`} className={p.side}>
                            <td>
                              <button type="button" className="dsc-link" onClick={() => selectMarket(p)}>
                                {p.symbol}
                                {p.leverage ? <small> {p.leverage}x</small> : null}
                                {p.dex !== 'main' ? <small> {p.dex}</small> : null}
                              </button>
                            </td>
                            <td className={p.side === 'long' ? 'pos' : 'neg'}>
                              {p.side === 'long' ? '' : '−'}
                              {fmtSz(p.sizeContracts)} {p.symbol}
                            </td>
                            <td>{fmtUsd(p.positionValue)}</td>
                            <td>{fmtPx(p.entryPrice, p.entryPrice)}</td>
                            <td>{fmtPx(p.markPrice, p.entryPrice)}</td>
                            <td className={`dsc-pnl-cell ${pnl >= 0 ? 'pos' : 'neg'}`}>{fmtPnlRoe(pnl, roe)}</td>
                            <td>{p.liquidationPx ? fmtPx(p.liquidationPx, p.entryPrice) : '—'}</td>
                            <td>{p.marginUsed != null ? fmtUsd(p.marginUsed) : '—'}</td>
                            <td>
                              <button type="button" className="dsc-mini" disabled={busy} onClick={() => closePosition(p)}>
                                {sameMarketAsTicket(p) && (orderType === 'limit' || orderType === 'post_only')
                                  ? 'Limit close'
                                  : 'Close'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                      {!positions.length && (
                        <tr>
                          <td colSpan={9} className="dsc-muted">
                            No Disc Scalping positions
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {tab === 'orders' && (
              <div className="dsc-table-scroll">
              {!paperMode && (
                <p className="dsc-history-note">Disc Scalping working orders only — not Omni, MAP, or other desks.</p>
              )}
              <table className="dsc-table">
                <thead>
                  <tr><th>Market</th><th>Side</th><th>Type</th><th>Price</th><th>Size</th><th>RO</th><th>Time</th><th /></tr>
                </thead>
                <tbody>
                  {(account?.openOrders || []).map((o) => (
                    <tr key={o.oid}>
                      <td>{o.symbol || o.coin}<small>{o.dex && o.dex !== 'main' ? ` ${o.dex}` : ''}</small></td>
                      <td className={o.side === 'buy' ? 'pos' : 'neg'}>{o.side}</td>
                      <td>{o.orderType || o.tif || 'limit'}</td>
                      <td>{fmtPx(Number(o.price), Number(o.price))}</td>
                      <td>{fmtSz(Number(o.size))}{o.origSize && o.origSize !== o.size ? <small> / {fmtSz(Number(o.origSize))}</small> : null}</td>
                      <td>{o.reduceOnly ? 'yes' : ''}</td>
                      <td>{fmtTime(o.timestamp)}</td>
                      <td><button type="button" className="dsc-mini" disabled={busy} onClick={() => cancelOrder(o)}>Cancel</button></td>
                    </tr>
                  ))}
                  {!account?.openOrders?.length && (
                    <tr><td colSpan={8} className="dsc-muted">No Disc Scalping open orders</td></tr>
                  )}
                </tbody>
              </table>
              </div>
            )}

            {tab === 'history' && (
              <div className="dsc-table-scroll">
              <p className="dsc-history-note">Disc Scalping orders only — not Omni, MAP, or other desks.</p>
              <table className="dsc-table">
                <thead>
                  <tr><th>Date / Time</th><th>Market</th><th>Side</th><th>Type</th><th>Size</th><th>Price</th><th>Fill</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {(account?.recentOrders || []).map((o) => (
                    <tr key={o.id} className={o.status === 'failed' ? 'failed' : ''}>
                      <td className="dsc-dt">{fmtDateTime(o.executed_at || o.created_at)}</td>
                      <td>{o.symbol}{o.dex && o.dex !== 'main' ? <small> {o.dex}</small> : null}{o.is_paper ? <small> paper</small> : null}</td>
                      <td className={o.side === 'buy' ? 'pos' : 'neg'}>{o.side}{o.reduce_only ? ' (ro)' : ''}</td>
                      <td>{o.order_type}</td>
                      <td>{fmtSz(Number(o.filled_contracts || o.size_contracts))}</td>
                      <td>{o.limit_price != null ? fmtPx(Number(o.limit_price), Number(o.limit_price)) : '—'}</td>
                      <td>{o.fill_price != null ? fmtPx(Number(o.fill_price), Number(o.fill_price)) : '—'}</td>
                      <td title={o.error_message || ''}>{o.status}{o.error_message ? ' ⚠' : ''}</td>
                    </tr>
                  ))}
                  {!account?.recentOrders?.length && <tr><td colSpan={8} className="dsc-muted">No Disc Scalping orders yet</td></tr>}
                </tbody>
              </table>
              </div>
            )}

            {/* Always mounted so unread counts and signal toasts keep flowing while trading. */}
            <div className="dsc-chat-host" style={tab === 'chat' ? undefined : { display: 'none' }}>
              <DeskChat
                visible={tab === 'chat'}
                isAdmin={!!access?.isAdmin}
                market={{ symbol: market.symbol, dex: market.dex, coin: meta?.coin || market.coin }}
                mid={mid}
                onLoadSignal={loadSignalIntoTicket}
                onUnread={setChatUnread}
                onToast={showToast}
              />
            </div>
          </div>
        </section>
      </div>

      {toast && <div className={`dsc-toast ${toast.kind}`}>{toast.text}</div>}

      <WalletGearModal
        open={walletOpen}
        onClose={() => {
          setWalletOpen(false);
          refreshSoon();
        }}
        title="Hyperliquid wallet & agent"
        view="manual"
        apiPrefix="single-asset-hl"
      />
    </div>
  );
}
